import React, { ReactElement } from 'react';
import moment from 'moment-timezone';
import { Link } from 'react-router-dom';
import {
  BarController,
  BarElement,
  CategoryScale,
  Chart,
  LinearScale,
  Tooltip,
  TooltipItem,
} from 'chart.js';
import { Chart as ReactChart } from 'react-chartjs-2';
import { ChartSimpleIcon, ChevronRightIcon } from 'components/icons';
import { TimeSeries } from 'apollo/generated/client-operations';
import { cn } from 'utils';
import { OdinChartColorDescriptor } from 'containers/reportDashboard/types';
import {
  peakTimeSeriesValue,
  STAT_PLACEHOLDER,
} from 'containers/jobsite/tabs/jobsiteOverview/helpers';
import {
  JobsiteOverviewEmptyState,
  Shimmer,
} from 'containers/jobsite/tabs/jobsiteOverview/components/JobsiteOverviewStates';

Chart.register(BarController, BarElement, CategoryScale, LinearScale, Tooltip);

export type JobsiteOverviewTimeSeriesChartStatValue =
  | string
  | number
  | ((data: TimeSeries[]) => string | number)
  | null
  | undefined;

export type JobsiteOverviewTimeSeriesChartStat = {
  label: string;
  value: JobsiteOverviewTimeSeriesChartStatValue;
};

export type JobsiteOverviewTimeSeriesChartProps = {
  title: string;
  linkTo?: string;
  linkLabel?: string;
  data: TimeSeries[];
  color: OdinChartColorDescriptor;
  stats?: JobsiteOverviewTimeSeriesChartStat[];
  loading?: boolean;
  emptyMessage?: string;
};

// Mirrors the chart's `aspectRatio: 2.5`, so the loading/empty placeholders
// occupy the same footprint as the rendered chart and the card doesn't reflow
// once data arrives.
const chartAreaClasses = 'odin-w-full odin-aspect-[5/2]';

export function JobsiteOverviewTimeSeriesChart(
  props: JobsiteOverviewTimeSeriesChartProps,
): ReactElement {
  const {
    title,
    linkTo,
    linkLabel = 'Hourly count',
    data,
    color,
    stats = [],
    loading = false,
    emptyMessage = 'No activity on this date',
  } = props;

  // The time series returns one bucket per hour, so an inactive day is an
  // array of zeros, not an empty array — treat a zero peak as "no data".
  const isEmpty = !loading && peakTimeSeriesValue(data) === 0;

  // Render on a category axis (the date-fns time adapter has no time-zone
  // support). `dateTime` is the bucket's start as a UTC instant, but the query
  // buckets sessions in the jobsite's local day (generate_series ... AT TIME
  // ZONE time_zone), so each instant is really a jobsite-local hour boundary.
  // Format the labels in the jobsite `timeZone` (NOT the viewer's zone) so an
  // 8:21am PDT clock-in lands in the 8am bucket for every viewer — an admin in
  // another zone must still see the site's local hours, which is what the
  // caption promises. Labeling by interval_start (not interval_end) keeps an
  // 8:21 swipe in the 8:00 bucket rather than 9:00.
  const timeZone = data[0]?.timeZone ?? null;
  const hourLabels = React.useMemo(
    () =>
      data.map((point) =>
        timeZone
          ? moment.tz(point.dateTime, timeZone).format('ha')
          : moment(point.dateTime).format('ha'),
      ),
    [data, timeZone],
  );
  const values = React.useMemo(() => data.map((point) => point.value), [data]);
  // Abbreviation for the jobsite zone on the displayed day (e.g. PST vs PDT)
  // for the caption label.
  const timeZoneAbbrev = timeZone
    ? moment.tz(data[0]?.dateTime, timeZone).format('z')
    : null;

  let chartAreaContent: ReactElement;
  if (loading) {
    chartAreaContent = <Shimmer className={chartAreaClasses} />;
  } else if (isEmpty) {
    chartAreaContent = (
      <JobsiteOverviewEmptyState
        icon={ChartSimpleIcon}
        message={emptyMessage}
        className={chartAreaClasses}
      />
    );
  } else {
    chartAreaContent = (
      <div className="odin-flex-grow odin-min-w-0">
        <ReactChart
          type="bar"
          options={{
            responsive: true,
            aspectRatio: 2.5,
            plugins: {
              legend: { display: false },
              tooltip: {
                enabled: true,
                callbacks: {
                  label: (ctx: TooltipItem<'bar'>): string => `${ctx.parsed.y}`,
                },
              },
            },
            scales: {
              x: {
                type: 'category',
                grid: { display: false },
                border: { display: false },
                ticks: {
                  // Label every other hour (1AM, 3AM, …) to mirror the previous
                  // time axis without crowding 24 category ticks. Return '' (not
                  // undefined) for hidden ticks so Chart.js suppresses the label
                  // rather than falling back to the default formatter.
                  callback(_value, index): string {
                    return index % 2 === 1 ? hourLabels[index] : '';
                  },
                  autoSkip: false,
                  maxRotation: 0,
                  color: '#001024',
                  font: { size: 11 },
                },
              },
              y: {
                display: false,
                beginAtZero: true,
                grid: { display: false },
              },
            },
          }}
          data={{
            labels: hourLabels,
            datasets: [
              {
                label: title,
                data: values,
                backgroundColor: (context): CanvasGradient | null => {
                  const { ctx, chartArea } = context.chart;
                  if (!chartArea) return null;

                  const gradient = ctx.createLinearGradient(
                    0,
                    chartArea.bottom,
                    0,
                    chartArea.top,
                  );
                  gradient.addColorStop(0, `rgba(${color.backgroundRgb}, 0.5)`); // Bottom color
                  gradient.addColorStop(1, `rgba(${color.backgroundRgb},  1)`); // Top color

                  return gradient;
                },
                borderColor: color.border,
                borderRadius: 8,
                borderSkipped: false,
                categoryPercentage: 0.7,
                barPercentage: 0.9,
              },
            ],
          }}
        />
      </div>
    );
  }

  return (
    <div
      className={cn(
        'odin-flex odin-flex-col odin-gap-5',
        'odin-rounded-2xl odin-border odin-border-odin-medium-gray',
        'odin-bg-odin-white odin-p-6',
        'odin-min-w-0',
      )}
    >
      <div className="odin-flex odin-flex-col odin-gap-1">
        <div className="odin-flex odin-items-center odin-justify-between odin-gap-3">
          <span className="odin-text-lg odin-font-semibold odin-text-odin-navy">
            {title}
          </span>
          {linkTo && (
            <Link
              to={linkTo}
              className={cn(
                'odin-flex odin-items-center odin-gap-1.5',
                'odin-text-sm odin-text-odin-navy',
                'hover:odin-text-odin-primary',
              )}
            >
              <span>{linkLabel}</span>
              <ChevronRightIcon className="odin-w-3 odin-h-3" />
            </Link>
          )}
        </div>
        {!loading && timeZoneAbbrev && (
          <span className="odin-text-sm odin-text-odin-dark-gray">
            {`Times shown in site time zone (${timeZoneAbbrev})`}
          </span>
        )}
      </div>
      <div className="odin-w-full odin-flex odin-justify-center">
        {chartAreaContent}
      </div>
      {stats.length > 0 && (
        <div className="odin-flex odin-items-center odin-gap-6">
          {stats.map(({ label, value }) => {
            // Resolve lazily so function-based stats aren't invoked while loading.
            const valueContent = loading ? (
              <Shimmer className="odin-h-6 odin-w-10 odin-my-0.5" />
            ) : (
              <span className="odin-text-lg odin-font-semibold odin-text-odin-navy">
                {(typeof value === 'function' ? value(data) : value) ??
                  STAT_PLACEHOLDER}
              </span>
            );
            return (
              <div
                key={label}
                className="odin-flex odin-flex-row odin-grow odin-justify-start"
              >
                <div className="odin-flex odin-flex-col odin-gap-0.5 odin-items-start">
                  {valueContent}
                  <span className="odin-text-sm odin-text-odin-navy">
                    {label}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
